import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, combineLatest, concatMap, distinctUntilChanged, map, of, Subject, switchMap } from 'rxjs';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { DashboardToolbarComponent } from './components/dashboard-toolbar/dashboard-toolbar.component';
import { LivePipelineSectionComponent } from './sections/live-pipeline/live-pipeline-section.component';
import { TestmanagerKpisComponent } from './sections/testmanager-kpis/testmanager-kpis.component';
import { TestProgressComponent } from './sections/test-progress/test-progress.component';
import { TopFailuresComponent } from './sections/top-failures/top-failures.component';
import {
  ProjectFunnel,
  RequirementsFunnelComponent,
} from './sections/requirements-funnel/requirements-funnel.component';
import { QualitaetSectionComponent } from './sections/quality/quality.component';
import { DevOpsKpisComponent } from './sections/devops-kpis/devops-kpis.component';
import { DevOpsPipelineHealthComponent } from './sections/devops-pipeline-health/devops-pipeline-health.component';
import { DevOpsBuildsIncidentsComponent } from './sections/devops-builds-incidents/devops-builds-incidents.component';
import { ActivityFeedComponent } from './sections/activity-feed/activity-feed.component';
import { FreigabeStatusComponent } from './sections/freigabe-status/freigabe-status.component';
import { LiveReconnectHintComponent } from '../components/live-reconnect-hint/live-reconnect-hint.component';
import { DashboardEmptyAllHiddenComponent } from './sections/empty-states/dashboard-empty-all-hidden.component';
import { DashboardEmptyNoProjectComponent } from './sections/empty-states/dashboard-empty-no-project.component';
import { DashboardDataService } from './services/dashboard-data.service';
import { DashboardConfigService } from './services/dashboard-config.service';
import { AccountService } from '../account/account.service';
import { DashboardResponse, RequirementsFunnelResponse } from './models/dashboard.model';
import { DASH_SECTION_KEYS, DashboardConfig, DashboardSectionKey } from './models/dashboard-config.model';
import { daysAgoIso } from '../shared/date.util';
import { ProjectService } from '../services/project.service';
import { ProjectOption, ProjectsState } from './components/projekt-filter/projekt-filter.component';
import { sameIds, selectionWithin } from './models/project-selection';

/**
 * Default-Fenster: letzte 7 Tage inklusive heute. Muss zum BE-Default passen,
 * sonst kollabiert der Range-Filter auf 1 Tag und die Sparklines (min. 2 Punkte)
 * verschwinden.
 */
const DEFAULT_WINDOW_DAYS = 7;

@Component({
  selector: 'app-dashboard-page',
  imports: [
    FreigabeStatusComponent,
    DashboardToolbarComponent,
    LivePipelineSectionComponent,
    TestmanagerKpisComponent,
    TestProgressComponent,
    TopFailuresComponent,
    RequirementsFunnelComponent,
    QualitaetSectionComponent,
    DevOpsKpisComponent,
    DevOpsPipelineHealthComponent,
    DevOpsBuildsIncidentsComponent,
    ActivityFeedComponent,
    DashboardEmptyAllHiddenComponent,
    DashboardEmptyNoProjectComponent,
    LiveReconnectHintComponent,
  ],
  templateUrl: './dashboard-page.component.html',
  styleUrl: './dashboard-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardPageComponent {
  private dataService = inject(DashboardDataService);
  private configService = inject(DashboardConfigService);
  private accountService = inject(AccountService);
  private projectService = inject(ProjectService);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  protected readonly username = signal('');
  protected readonly config = signal<DashboardConfig | null>(null);
  protected readonly data = signal<DashboardResponse | null>(null);
  protected readonly projectFunnels = signal<ProjectFunnel[]>([]);
  protected readonly von = signal<string | null>(daysAgoIso(DEFAULT_WINDOW_DAYS - 1));
  protected readonly bis = signal<string | null>(null);
  protected readonly configOpen = signal(false);

  protected readonly projects = signal<ProjectOption[]>([]);
  protected readonly projectsState = signal<ProjectsState>('loading');
  protected readonly colorOrder = computed(() => this.projects().map((project) => project.id));

  /**
   * Die Auswahl steht neben der Config, weil sie ein fehlgeschlagenes Speichern ueberlebt:
   * die Config springt auf den letzten gespeicherten Stand zurueck, die Auswahl bleibt in der
   * Sitzung aktiv.
   */
  private readonly storedProjectIds = signal<string[]>([]);

  protected readonly selectedProjectIds = computed(
    () =>
      this.projectsState() === 'loaded'
        ? selectionWithin(this.storedProjectIds(), this.colorOrder())
        : this.storedProjectIds(),
    { equal: sameIds },
  );

  /** Ohne Projektliste laesst sich die Auswahl nicht anzeigen; die Bereiche laden dann ungefiltert. */
  protected readonly effectiveProjectIds = computed(
    () => (this.projectsState() === 'error' ? [] : this.selectedProjectIds()),
    { equal: sameIds },
  );

  private readonly dashboardFailed = signal(false);
  private readonly funnelFailed = signal(false);
  protected readonly showsStaleData = computed(() => this.dashboardFailed() || this.funnelFailed());

  protected readonly hasNoProjects = computed(() => {
    const d = this.data();
    return d != null && !d.userHasProjects;
  });

  protected readonly allSectionsHidden = computed(() => {
    const cfg = this.config();
    return cfg != null && DASH_SECTION_KEYS.every((key) => !cfg.sections[key]);
  });

  private readonly snackError: MatSnackBarConfig = { panelClass: 'snackbar-error' };
  private lastPersisted: DashboardConfig | null = null;
  private readonly saveQueue = new Subject<DashboardConfig>();

  constructor() {
    this.accountService
      .getProfile()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(),
      )
      .subscribe((profile) => {
        if (profile) this.username.set(profile.displayName);
      });

    this.projectService
      .listForCurrentUser()
      .pipe(
        map((projects) => ({ projects: projects.map(({ id, name }) => ({ id, name })) })),
        catchError(() => of({ projects: null })),
        takeUntilDestroyed(),
      )
      .subscribe(({ projects }) => {
        if (projects) {
          this.projects.set(projects);
          this.projectsState.set('loaded');
        } else {
          this.projectsState.set('error');
        }
      });

    const effectiveProjectIds$ = toObservable(this.effectiveProjectIds);

    // Poll gehaengt an das Config-Signal, nicht an eine zweite Subscription auf
    // `getConfig()` — `getConfig()` liefert genau eine Emission und completed danach, was
    // beim Off-Start den Stream beendet haette: ein spaeteres Einschalten der Sektion
    // haette die Daten nie nachgeladen. Vor dem ersten Config-Load faellt der Wert auf
    // `true`, damit die Erstansicht nicht kuenstlich leer bleibt; ein zwischenzeitliches
    // Umschalten canceled den laufenden Poll (`switchMap`) und startet ihn beim
    // Wiedereinschalten neu. `distinctUntilChanged` verhindert, dass ein Config-Write
    // ohne Aenderung an dieser Sektion den Poll unnoetig neu aufsetzt.
    combineLatest([
      toObservable(computed(() => this.config()?.sections.testFunnel ?? true)).pipe(distinctUntilChanged()),
      effectiveProjectIds$,
    ])
      .pipe(
        switchMap(([visible, projectIds]) =>
          visible
            ? this.dataService.pollRequirementsFunnel(projectIds)
            : of<RequirementsFunnelResponse>({ projects: [] }),
        ),
        takeUntilDestroyed(),
      )
      .subscribe((response) => {
        this.funnelFailed.set(response === null);
        if (response) this.projectFunnels.set(response.projects ?? []);
      });

    this.configService
      .getConfig()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(),
      )
      .subscribe((config) => {
        if (config) {
          this.config.set(config);
          this.lastPersisted = config;
          this.von.set(config.vonDatum ?? daysAgoIso(DEFAULT_WINDOW_DAYS - 1));
          this.bis.set(config.bisDatum);
          this.storedProjectIds.set(config.projectIds ?? []);
        }
      });

    this.saveQueue
      .pipe(
        concatMap((config) =>
          this.configService.saveConfig(config).pipe(
            map((saved) => ({ saved })),
            catchError(() => of({ saved: null })),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe(({ saved }) => {
        if (saved) {
          this.lastPersisted = saved;
        } else {
          if (this.lastPersisted) this.config.set(this.lastPersisted);
          this.notifyError('liveDashboard.panel.saveError');
        }
      });

    combineLatest([toObservable(this.von), toObservable(this.bis), effectiveProjectIds$])
      .pipe(
        switchMap(([von, bis, projectIds]) => this.dataService.poll(von ?? undefined, bis ?? undefined, projectIds)),
        takeUntilDestroyed(),
      )
      .subscribe((response) => {
        this.dashboardFailed.set(response === null);
        if (response) this.data.set(response);
      });
  }

  protected onVonChange(value: string): void {
    this.von.set(value);
  }

  protected onBisChange(value: string): void {
    this.bis.set(value);
  }

  protected onToggleConfig(): void {
    this.configOpen.update((open) => !open);
  }

  protected onCloseConfig(): void {
    this.configOpen.set(false);
  }

  protected onSectionToggle(key: DashboardSectionKey): void {
    const current = this.config();
    if (!current) return;

    this.save({
      ...current,
      sections: { ...current.sections, [key]: !current.sections[key] },
      projectIds: this.storedProjectIds(),
    });
  }

  protected onProjectIdsChange(projectIds: string[]): void {
    this.storedProjectIds.set(projectIds);
    const current = this.config();
    if (!current) return;

    this.save({ ...current, projectIds });
  }

  private save(config: DashboardConfig): void {
    const updated: DashboardConfig = { ...config, isDefault: this.configService.isDefault(config) };
    this.config.set(updated);
    this.saveQueue.next(updated);
  }

  protected onResetToDefault(): void {
    this.configService
      .resetConfig()
      .pipe(
        map((config) => ({ config })),
        catchError(() => of({ config: null })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ config }) => {
        if (config) {
          this.config.set(config);
          this.lastPersisted = config;
          this.storedProjectIds.set(config.projectIds ?? []);
        } else {
          this.notifyError('liveDashboard.panel.resetError');
        }
      });
  }

  private notifyError(key: string): void {
    this.snackBar.open(this.translate.instant(key), undefined, this.snackError);
  }

  protected isDefault(): boolean {
    const config = this.config();
    if (!config) return true;
    if (sameIds(config.projectIds ?? [], this.storedProjectIds())) return config.isDefault;
    return this.configService.isDefault({ ...config, projectIds: this.storedProjectIds() });
  }
}
